// src/components/AuthModal.js

import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Form, Input, Button, message } from 'antd';
import { useDispatch } from 'react-redux';
import { setUser } from '../store/interfaceSlice';
import axios from '../services/axiosConfig';

const AuthModal = ({ open, onClose, onAuthSuccess }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setUsername('');
      setPassword('');
      setRegisterUsername('');
      setRegisterPassword('');
      setRegisterConfirmPassword('');
    }
  }, [open]);

  const handleLogin = async () => {
    if (username && password) {
      setLoading(true);
      try {
        const response = await axios.post('/api/v1/auth/login', {
          username,
          password
        });

        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          const userData = response.data.user || { id: null, username };
          dispatch(setUser({
            id: userData.id,
            username: userData.username || username,
            role: userData.role || 'user',
          }));
          if (userData.is_active === false) {
            message.info('Вход выполнен. Для доступа к сервису дождитесь активации учётной записи.');
          } else {
            message.success('Успешный вход!');
          }
          if (onAuthSuccess) {
            onAuthSuccess(response.data.token);
          }
          onClose();
        }
      } catch (error) {
        console.error('Ошибка при входе:', error);
        if (error.response?.status === 401) {
          message.error('Неверный логин или пароль');
        } else {
          message.error('Ошибка сервера при входе');
        }
      } finally {
        setLoading(false);
      }
    } else {
      message.warning('Пожалуйста, заполните все поля');
    }
  };

  const handleRegister = async () => {
    if (registerUsername && registerPassword && registerConfirmPassword) {
      if (registerPassword !== registerConfirmPassword) {
        message.error('Пароли не совпадают');
        return;
      }
      
      setRegisterLoading(true);
      try {
        await axios.post('/api/v1/auth/register', {
          username: registerUsername,
          password: registerPassword
        });
        
        message.success('Регистрация успешна! Для доступа к сервису дождитесь активации учётной записи администратором.');
        setActiveTab('login');
        setRegisterUsername('');
        setRegisterPassword('');
        setRegisterConfirmPassword('');
      } catch (error) {
        console.error('Ошибка при регистрации:', error);
        if (error.response?.status === 409) {
          message.error('Пользователь с таким именем уже существует');
        } else {
          message.error('Ошибка при регистрации');
        }
      } finally {
        setRegisterLoading(false);
      }
    } else {
      message.warning('Пожалуйста, заполните все поля');
    }
  };

  const changeTab = (key) => {
    setActiveTab(key);
  };

  const items = [
    {
      key: 'login',
      label: 'Вход',
      children: (
        <Form layout="vertical" onFinish={handleLogin}>
          <Form.Item label="Логин">
            <Input
              placeholder="Введите логин"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Пароль">
            <Input.Password
              placeholder="Введите пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Войти
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'register',
      label: 'Регистрация',
      children: (
        <Form layout="vertical" onFinish={handleRegister}>
          <Form.Item
            label="Имя пользователя"
            rules={[{ required: true, message: 'Пожалуйста, введите имя пользователя' }]}
          >
            <Input
              placeholder="Введите имя пользователя"
              value={registerUsername}
              onChange={(e) => setRegisterUsername(e.target.value)}
            />
          </Form.Item>
          <Form.Item
            label="Пароль"
            rules={[{ required: true, message: 'Пожалуйста, введите пароль' }]}
          >
            <Input.Password
              placeholder="Введите пароль"
              value={registerPassword}
              onChange={(e) => setRegisterPassword(e.target.value)}
            />
          </Form.Item>
          <Form.Item
            label="Подтверждение пароля"
            hasFeedback
            validateStatus={registerPassword && registerConfirmPassword ? (registerPassword === registerConfirmPassword ? 'success' : 'error') : ''}
            help={registerPassword && registerConfirmPassword && registerPassword !== registerConfirmPassword ? 'Пароли не совпадают' : ''}
          >
            <Input.Password
              placeholder="Подтвердите пароль"
              value={registerConfirmPassword}
              onChange={(e) => setRegisterConfirmPassword(e.target.value)}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={registerLoading} block>
              Зарегистрироваться
            </Button>
          </Form.Item>
        </Form>
      )
    }
  ];

  return (
    <Modal
      title="Авторизация"
      open={open}
      footer={null}
      closable={false}
      centered
    >
      <Tabs activeKey={activeTab} onChange={changeTab} destroyInactiveTabPane={false} items={items} />
    </Modal>
  );
};

export default AuthModal;
